import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Landmark, Calendar, DollarSign, X, Save, AlertCircle } from 'lucide-react';
import { getBanksByBuilding, createBank, updateBank, deleteBank } from '../../Fetch/fetchBank';
import styles from './BankStatus.module.css';

const BankStatus = () => {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const [formData, setFormData] = useState({
    bankName: '',
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    moneyLeft: ''
  });
  const [saving, setSaving] = useState(false);

  // Get building ID from localStorage
  const getBuildingId = () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      return userData?.buildingId;
    } catch {
      return null;
    }
  };

  const buildingId = getBuildingId();

  // Load banks on mount and when building changes
  useEffect(() => {
    if (buildingId) {
      loadBanks();
    }

    // Listen for building changes
    const handleBuildingChange = () => {
      loadBanks();
    };

    window.addEventListener('buildingChanged', handleBuildingChange);
    return () => window.removeEventListener('buildingChanged', handleBuildingChange);
  }, [buildingId]);

  const loadBanks = async () => {
    const currentBuildingId = getBuildingId();
    if (!currentBuildingId) {
      setError('לא נבחר בניין');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await getBanksByBuilding(currentBuildingId);
      setBanks(data || []);
    } catch (err) {
      console.error('Error loading banks:', err);
      setError('שגיאה בטעינת נתוני הבנק');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (bank = null) => {
    if (bank) {
      setEditingBank(bank);
      setFormData({
        bankName: bank.bankName,
        year: bank.year,
        month: bank.month,
        moneyLeft: bank.moneyLeft.toString()
      });
    } else {
      setEditingBank(null);
      setFormData({
        bankName: '',
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        moneyLeft: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingBank(null);
    setFormData({
      bankName: '',
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      moneyLeft: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const currentBuildingId = getBuildingId();
    if (!currentBuildingId) {
      alert('לא נבחר בניין');
      return;
    }

    if (!formData.bankName.trim()) {
      alert('יש להזין שם בנק');
      return;
    }

    if (!formData.moneyLeft || parseFloat(formData.moneyLeft) < 0) {
      alert('יש להזין סכום תקין');
      return;
    }

    try {
      setSaving(true);

      const bankData = {
        bankName: formData.bankName.trim(),
        year: parseInt(formData.year),
        month: parseInt(formData.month),
        moneyLeft: parseFloat(formData.moneyLeft),
        buildingID: currentBuildingId
      };

      if (editingBank) {
        await updateBank(editingBank.id, bankData);
      } else {
        await createBank(bankData);
      }

      handleCloseModal();
      loadBanks();
    } catch (err) {
      console.error('Error saving bank:', err);
      alert('שגיאה בשמירת הנתונים');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק רשומה זו?')) {
      return;
    }

    try {
      await deleteBank(id);
      loadBanks();
    } catch (err) {
      console.error('Error deleting bank:', err);
      alert('שגיאה במחיקת הרשומה');
    }
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const getMonthName = (month) => {
    const months = [
      'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
      'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'
    ];
    return months[month - 1] || '';
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>טוען נתונים...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <Landmark size={28} />
          <h1>סטטוס בנק</h1>
        </div>
        <button className={styles.addButton} onClick={() => handleOpenModal()}>
          <Plus size={20} />
          <span>הוסף רשומה</span>
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className={styles.errorMessage}>
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Summary Card */}
      {banks.length > 0 && (
        <div className={styles.summaryCard}>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>יתרה אחרונה</span>
            <span className={styles.summaryValue}>
              {formatMoney(banks[0]?.moneyLeft || 0)}
            </span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>בנק</span>
            <span className={styles.summaryValueSmall}>{banks[0]?.bankName || '-'}</span>
          </div>
          <div className={styles.summaryItem}>
            <span className={styles.summaryLabel}>תאריך עדכון</span>
            <span className={styles.summaryValueSmall}>
              {banks[0] ? `${getMonthName(banks[0].month)} ${banks[0].year}` : '-'}
            </span>
          </div>
        </div>
      )}

      {/* Table */}
      {banks.length === 0 ? (
        <div className={styles.emptyState}>
          <Landmark size={48} />
          <h3>אין רשומות בנק</h3>
          <p>לחץ על "הוסף רשומה" כדי להוסיף את היתרה הראשונה</p>
        </div>
      ) : (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>מזהה</th>
                <th>שם בנק</th>
                <th>שנה</th>
                <th>חודש</th>
                <th>יתרה</th>
                <th>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {banks.map((bank) => (
                <tr key={bank.id}>
                  <td data-label="מזהה">{bank.id}</td>
                  <td data-label="שם בנק">
                    <div className={styles.bankNameCell}>
                      <Landmark size={16} />
                      <span>{bank.bankName}</span>
                    </div>
                  </td>
                  <td data-label="שנה">{bank.year}</td>
                  <td data-label="חודש">{getMonthName(bank.month)}</td>
                  <td data-label="יתרה">
                    <span className={bank.moneyLeft >= 0 ? styles.positiveAmount : styles.negativeAmount}>
                      {formatMoney(bank.moneyLeft)}
                    </span>
                  </td>
                  <td data-label="פעולות">
                    <div className={styles.actions}>
                      <button
                        className={styles.editButton}
                        onClick={() => handleOpenModal(bank)}
                        title="עריכה"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        className={styles.deleteButton}
                        onClick={() => handleDelete(bank.id)}
                        title="מחיקה"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className={styles.modalOverlay} onClick={handleCloseModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>{editingBank ? 'עריכת רשומה' : 'הוספת רשומה חדשה'}</h2>
              <button className={styles.closeButton} onClick={handleCloseModal}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.formGroup}>
                <label htmlFor="bankName">
                  <Landmark size={16} />
                  שם בנק
                </label>
                <input
                  type="text"
                  id="bankName"
                  name="bankName"
                  value={formData.bankName}
                  onChange={handleInputChange}
                  placeholder="לדוגמה: בנק הפועלים"
                  required
                />
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="year">
                    <Calendar size={16} />
                    שנה
                  </label>
                  <select
                    id="year"
                    name="year"
                    value={formData.year}
                    onChange={handleInputChange}
                    required
                  >
                    {Array.from({ length: 10 }, (_, i) => {
                      const year = new Date().getFullYear() - 5 + i;
                      return (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="month">
                    <Calendar size={16} />
                    חודש
                  </label>
                  <select
                    id="month"
                    name="month"
                    value={formData.month}
                    onChange={handleInputChange}
                    required
                  >
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {getMonthName(i + 1)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="moneyLeft">
                  <DollarSign size={16} />
                  יתרה בבנק (₪)
                </label>
                <input
                  type="number"
                  id="moneyLeft"
                  name="moneyLeft"
                  value={formData.moneyLeft}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  step="0.01"
                  required
                />
              </div>

              <div className={styles.formActions}>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={handleCloseModal}
                  disabled={saving}
                >
                  ביטול
                </button>
                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <div className={styles.buttonSpinner}></div>
                      שומר...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      {editingBank ? 'עדכן' : 'הוסף'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankStatus;
